import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography
} from '@mui/material';
import UnitPageShell from '../../components/shared/UnitPageShell';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../../context/AuthContext';
import { apiConfig } from '../../utils/apiConfig';

const infoText = 'Teilen Sie Bilder und Hinweise aus dem Feld. Das Bild ist Pflicht, der Kommentar optional.';

const Page: React.FC = () => {
  const { token, canViewVorschlaege, canSubmitVorschlaege } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setMessage(null);
    setError(null);
  };

  const handleUpload = async () => {
    setMessage(null);
    setError(null);

    if (!canSubmitVorschlaege) {
      setError('Keine Berechtigung zum Einreichen von Vorschlaegen.');
      return;
    }

    if (!token) {
      setError('Bitte zuerst einloggen, um einen Vorschlag zu senden.');
      return;
    }

    if (!selectedFile) {
      setError('Bitte zuerst ein Bild auswählen.');
      return;
    }

    const formData = new FormData();
    formData.append('action', 'uploadUserImageSimple');
    formData.append('comment', comment.trim());
    formData.append('image_file', selectedFile);

    setUploading(true);
    try {
      const response = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data) {
        throw new Error(data?.error || `Serverfehler: ${response.status}`);
      }
      if (data.success) {
        setMessage(data.message || 'Upload erfolgreich.');
        setSelectedFile(null);
        setComment('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        throw new Error(data.error || 'Upload fehlgeschlagen.');
      }
    } catch (err: any) {
      setError(err?.message || 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  };

  if (!canViewVorschlaege) {
    return (
      <UnitPageShell title="Vorschläge & Feedback" showTTS={false}>
        <Box sx={{ maxWidth: 520, mx: 'auto', width: '100%' }}>
          <Alert severity="error">Keine Berechtigung fuer die Vorschlagsseite.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="Vorschläge & Feedback" showTTS={false}>
      <Box sx={{ maxWidth: 520, mx: 'auto', width: '100%' }}>
        <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 24px 46px rgba(13,53,89,0.12)' }}>
          <Stack spacing={3}>
            <Typography sx={{ color: '#384860', lineHeight: 1.7 }}>{infoText}</Typography>

            {message && <Alert severity="success">{message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? 'Anderes Bild wählen' : 'Bild auswählen'}
              </Button>
              {previewUrl && (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Vorschau"
                  sx={{
                    mt: 2,
                    width: '100%',
                    maxHeight: 260,
                    objectFit: 'contain',
                    borderRadius: 2,
                    border: '1px solid rgba(13,53,89,0.15)'
                  }}
                />
              )}
            </Box>

            <TextField
              label="Kommentar (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                endIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleUpload}
                disabled={uploading || !canSubmitVorschlaege}
              >
                {uploading ? 'Wird gesendet…' : 'Vorschlag senden'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </UnitPageShell>
  );
};

export default Page;
